import * as React from 'react';
import { type VariantProps } from 'class-variance-authority';
import { ChevronDown, LucideIcon } from 'lucide-react';
import { Slot as SlotPrimitive } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';
import { buttonVariants } from './button-variants';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  selected?: boolean;
  autoHeight?: boolean;
  underlined?: boolean;
  underline?: "solid" | "dashed";
  placeholder?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, selected, variant, shape, appearance, mode, size, autoHeight, underlined, underline, asChild = false, placeholder = false, ...props }, ref) => {
    const Comp = asChild ? SlotPrimitive : 'button';
    return (
      <Comp
        ref={ref} // Pass the ref to the Comp
        data-slot="button"
        className={cn(
          buttonVariants({
            variant,
            size,
            shape,
            appearance,
            mode,
            autoHeight,
            placeholder,
            underlined,
            underline,
            className,
          }),
          asChild && props.disabled && 'pointer-events-none opacity-50',
        )}
        {...(selected && { 'data-state': 'open' })}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

interface ButtonArrowProps extends React.SVGProps<SVGSVGElement> {
  icon?: LucideIcon; // Allows passing any Lucide icon
}

function ButtonArrow({ icon: Icon = ChevronDown, className, ...props }: ButtonArrowProps) {
  return <Icon data-slot="button-arrow" className={cn('ms-auto -me-1', className)} {...props} />;
}

export { Button, ButtonArrow };
export type { ButtonProps };
